import { useMemo } from 'react';
import { useParentSize } from '@visx/responsive';

import type { ColumnChartProps } from '../../types/ColumnChartProps';
import { extractXAxisConfig } from '../../utils/extractXAxisConfig';
import { extractYAxisConfig } from '../../utils/extractYAxisConfig';
import { estimateYAxisNumTicks } from '../../utils/tickCalculation';
import { useAutoMargin } from '../../utils/useAutoMargin';
import { ChartCanvas } from '../ChartProvider/ChartCanvas';
import { ChartProvider, useChartProviderState } from '../ChartProvider/ChartProvider';

import { BarHoverHighlight } from './BarHoverHighlight';
import { useColumnChartChildren } from './useColumnChartChildren';

import styles from './ColumnChart.module.css';

const IGNORE_PARENT_SIZE_DIMS: Array<'width' | 'top' | 'left'> = ['width', 'top', 'left'];

/**
 * Composable ColumnChart component built on @visx/xychart.
 *
 * A thin wrapper around XYChart that provides LaunchPad theming and a lightweight
 * context for child components to coordinate.
 *
 * @example
 * ```tsx
 * <ColumnChart height={400}>
 *   <XAxis dataKey="timestamp" dataType="time" />
 *   <YAxis dataType="time" />
 *   <Grid />
 *   <BarSeries
 *     seriesKey="latency"
 *     name="API Latency"
 *     data={data}
 *     dataKey="latency"
 *   />
 *   <Tooltip enabled />
 * </ColumnChart>
 * ```
 */
export function ColumnChart({
  data,
  referenceLineData,
  children,
  width,
  height,
  margin,
  autoMargin = true,
  ariaLabel = 'Column chart visualization',
  disableDragToZoom = false,
  isLoading = false,
  onZoomChange,
  onDataPointHover,
  emptyStateTitle,
  emptyStateSubtitle,
  xDataKey: xDataKeyProp,
  xDataType: xDataTypeProp,
  ...rest
}: ColumnChartProps) {
  const { parentRef, height: parentHeight } = useParentSize({
    initialSize: { height: typeof height === 'number' ? height : 0 },
    ignoreDimensions: IGNORE_PARENT_SIZE_DIMS,
  });

  const defaultNumTicks = useMemo(
    () => (parentHeight > 0 ? estimateYAxisNumTicks(parentHeight) : undefined),
    [parentHeight],
  );

  // Band scale configuration for consistent categorical spacing
  // paddingInner: adds space between bars (controls gap between categories)
  // paddingOuter: adds space before first bar and after last bar for better drag-to-zoom interaction at edges
  const xScaleConfig = useMemo(
    () => ({
      type: 'band' as const, // ColumnChart always uses band scale for consistent spacing
      paddingInner: 0.1, // 10% gap between bars
      paddingOuter: 0.2, // 20% gap before first bar and after last bar (extra space for edge interactions)
    }),
    [],
  );

  // Extract XAxis configuration from children BEFORE creating provider.
  // ColumnChart always uses 'band' scale — xScaleType from props is ignored.
  // dataType is used for tick label formatting.
  // Parent-provided xDataKey/xDataType win over <XAxis> child props, so callers that
  // hide the axis (showXAxis === false → no <XAxis> child) can still wire x correctly.
  const extracted = useMemo(() => extractXAxisConfig(children, 'column'), [children]);
  const xDataKey = xDataKeyProp ?? extracted.xDataKey;
  const dataType = xDataTypeProp ?? extracted.dataType;
  const { xScaleType, hasXAxis, xAxisLabelOffset } = extracted;

  const yAxisConfig = useMemo(
    () => extractYAxisConfig(children, referenceLineData, defaultNumTicks),
    [children, referenceLineData, defaultNumTicks],
  );

  // Provider computes yScaleConfig from visible data (auto-scales during zoom)
  const chartProviderValue = useChartProviderState({
    chartType: 'column',
    data,
    referenceLineData,
    disableDragToZoom,
    isLoading,
    xDataKey,
    xScaleType, // Always 'band' for ColumnChart
    xDataType: dataType,
    onZoomChange,
    onDataPointHover,
    yAxisConfig,
    emptyStateTitle,
    emptyStateSubtitle,
  });

  const yScaleDomain = chartProviderValue.yScaleConfig?.domain;

  const chartMargin = useAutoMargin({
    hasXAxis,
    xAxisLabelOffset,
    hasYAxis: yAxisConfig.hasYAxis,
    yTickValues: chartProviderValue.yTickValues,
    yScaleDomain,
    yAxisDataType: yAxisConfig.yAxisDataType,
    yAxisTickFormat: yAxisConfig.yAxisTickFormat,
    enabled: autoMargin,
    manualMargin: margin,
  });

  // Reorder children to ensure XAxis renders last (so baseline appears solid, not covered by other elements)
  const reorderedChildren = useColumnChartChildren(children);

  return (
    <div
      ref={parentRef}
      className={styles.container}
      style={{ width: width ?? '100%', height: height ?? '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <ChartProvider value={chartProviderValue}>
        <ChartCanvas
          height={typeof height === 'number' ? height : undefined}
          width={typeof width === 'number' ? width : undefined}
          margin={chartMargin}
          xScale={xScaleConfig} // Band scale for consistent spacing (margins handle axis overlap)
          {...rest}
        >
          <BarHoverHighlight enabled />
          {reorderedChildren}
        </ChartCanvas>
      </ChartProvider>
    </div>
  );
}
